import React, { useEffect, useState, useCallback } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Search, Shield, Car, FileText, Clock, CheckCircle, AlertTriangle,
  RefreshCw, Settings, History, Calendar, Database, ExternalLink,
  Eye, Save, Trash2, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');
const apiCall = async (path, options = {}) => {
  const res = await fetch(`${API}/api${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}`, ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(typeof data?.detail === 'string' ? data.detail : 'Errore');
  return data;
};

const ANIA_LOGIN_URL = "https://amlogin.allianz.it/nidp/idff/sso?id=6&sid=0&option=credential&sid=0&target=https%3A%2F%2Fportaleagenzie.allianz.it%2Fmotor%2Finquiry%2Fania%2F%3FLDAPEDIR%3Dcn%253DSCHILIROSA.0712%2Cou%253Dusers%2Co%253Dmetaras";
const ANIA_DIRECT_URL = "https://portaleagenzie.allianz.it/motor/inquiry/ania/?LDAPEDIR=cn%3DSCHILIROSA.0712,ou%3Dusers,o%3Dmetaras";

const fmtDate = (d) => d ? format(new Date(d), 'dd/MM/yyyy HH:mm', { locale: it }) : '—';
const fmtDateShort = (d) => d ? format(new Date(d), 'dd/MM/yyyy', { locale: it }) : '—';

export default function AniaAllianzPage() {
  const [activeTab, setActiveTab] = useState('search');
  const [stats, setStats] = useState(null);
  const [mySearches, setMySearches] = useState([]);
  const [allSearches, setAllSearches] = useState([]);
  const [expiries, setExpiries] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Quick save form
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({
    plate: '', vehicle_type: '', brand: '', model: '',
    policy_company: '', policy_number: '', policy_expiry: '',
    cu_class: '', reason: 'Consultazione ANIA',
  });
  const [saving, setSaving] = useState(false);

  // Settings
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [portalUrl, setPortalUrl] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['super_admin', 'dipendente'].includes(user.ruolo);

  const loadStats = useCallback(async () => {
    try { const d = await apiCall('/ania-allianz/dashboard-stats'); setStats(d); } catch {}
  }, []);
  const loadMySearches = useCallback(async () => {
    try { const d = await apiCall('/ania-allianz/my-searches'); setMySearches(d.items || []); } catch {}
  }, []);
  const loadAllSearches = useCallback(async () => {
    if (!isAdmin) return;
    try { const d = await apiCall('/ania-allianz/all-searches'); setAllSearches(d.items || []); } catch {}
  }, [isAdmin]);
  const loadExpiries = useCallback(async () => {
    try { const d = await apiCall('/ania-allianz/expiries'); setExpiries(d.items || []); } catch {}
  }, []);

  useEffect(() => {
    loadStats(); loadMySearches(); loadExpiries();
    if (isAdmin) loadAllSearches();
  }, [loadStats, loadMySearches, loadAllSearches, loadExpiries, isAdmin]);

  const openAnia = () => {
    window.open(ANIA_LOGIN_URL, '_blank', 'noopener');
  };

  const openAniaWithPlate = (plate) => {
    // Open ANIA and copy plate to clipboard
    if (plate) {
      navigator.clipboard.writeText(plate.toUpperCase().replace(/\s/g, '')).then(() => {
        toast.success(`Targa ${plate} copiata! Incollala nel portale ANIA`);
      }).catch(() => {});
    }
    window.open(ANIA_DIRECT_URL, '_blank', 'noopener');
  };

  const handleSave = async () => {
    if (!saveForm.plate || saveForm.plate.replace(/\s/g, '').length < 5) {
      toast.error('Inserisci una targa valida'); return;
    }
    if (!saveForm.reason || saveForm.reason.length < 3) {
      toast.error('Inserisci il motivo'); return;
    }
    setSaving(true);
    try {
      await apiCall('/ania-allianz/save-vehicle', {
        method: 'POST',
        body: JSON.stringify(saveForm),
      });
      toast.success('Dati salvati!');
      setSaveOpen(false);
      setSaveForm({ plate: '', vehicle_type: '', brand: '', model: '', policy_company: '', policy_number: '', policy_expiry: '', cu_class: '', reason: 'Consultazione ANIA' });
      loadStats(); loadMySearches(); loadExpiries();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleViewVehicle = async (plate) => {
    try {
      const d = await apiCall(`/ania-allianz/vehicle/${plate}`);
      setSelectedVehicle(d);
    } catch (err) { toast.error(err.message); }
  };

  const loadPortalUrl = async () => {
    try {
      const d = await apiCall('/ania-allianz/credentials');
      setPortalUrl(d.portal_url || ANIA_LOGIN_URL);
    } catch {}
  };

  const TABS = [
    { key: 'search', label: 'Ricerca', icon: Search },
    { key: 'archive', label: 'Archivio', icon: Database },
    { key: 'expiries', label: 'Scadenze', icon: Calendar },
    ...(isAdmin ? [{ key: 'all-searches', label: 'Tutte le Ricerche', icon: Eye }] : []),
  ];

  // Get unique vehicles from my searches
  const myVehicles = [];
  const seenPlates = new Set();
  for (const s of mySearches) {
    if (!seenPlates.has(s.normalized_plate)) {
      seenPlates.add(s.normalized_plate);
      myVehicles.push(s);
    }
  }

  return (
    <div className="p-3 sm:p-6 md:p-8" data-testid="ania-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Database size={24} className="text-blue-600" /> ANIA
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Consultazione e archivio banca dati ANIA</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={openAnia} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-open-ania">
            <ExternalLink size={16} className="mr-1.5" /> Apri ANIA
          </Button>
          <Button variant="outline" onClick={() => { setSaveForm({ ...saveForm, plate: '' }); setSaveOpen(true); }} data-testid="btn-save-data">
            <Save size={16} className="mr-1.5" /> Salva Dati
          </Button>
          {isAdmin && (
            <Button size="sm" variant="ghost" onClick={() => { loadPortalUrl(); setSettingsOpen(true); }} data-testid="btn-settings">
              <Settings size={16} />
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">Veicoli Salvati</p>
            <p className="text-2xl font-bold text-slate-900">{stats.total_vehicles}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">Ricerche Totali</p>
            <p className="text-2xl font-bold text-slate-900">{stats.total_searches}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">Ricerche Oggi</p>
            <p className="text-2xl font-bold text-blue-600">{stats.today_searches}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">Portale</p>
              <p className="text-sm font-semibold text-slate-700">ANIA</p>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={openAnia}>
              <ExternalLink size={12} className="mr-1" /> Apri
            </Button>
          </div>
        </div>
      )}

      {/* Quick Search + Open */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs text-slate-500">Cerca targa e apri ANIA</Label>
            <div className="relative mt-1">
              <Car size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="ania-plate-input" placeholder="AA000BB" className="pl-10 h-10 text-base font-mono font-bold tracking-wider"
                data-testid="input-plate"
                onKeyDown={e => { if (e.key === 'Enter') { openAniaWithPlate(e.target.value); } }} />
            </div>
          </div>
          <Button onClick={() => {
            const v = document.getElementById('ania-plate-input')?.value || '';
            openAniaWithPlate(v);
          }} className="h-10 bg-blue-600 hover:bg-blue-700 px-6" data-testid="btn-search-ania">
            <ExternalLink size={16} className="mr-1.5" /> Cerca su ANIA
          </Button>
          <Button variant="outline" onClick={() => {
            const v = document.getElementById('ania-plate-input')?.value || '';
            setSaveForm({ ...saveForm, plate: v.toUpperCase().replace(/\s/g, '') });
            setSaveOpen(true);
          }} className="h-10" data-testid="btn-save-result">
            <Save size={16} className="mr-1.5" /> Salva Risultato
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Inserisci la targa, clicca "Cerca su ANIA" — si apre il portale in una nuova tab con la targa copiata. Dopo la consultazione, torna qui e clicca "Salva Risultato".</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0.5 sm:gap-1 mb-6 border-b border-slate-200 overflow-x-auto no-scrollbar">
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap ${activeTab === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
            data-testid={`tab-${tab.key}`}>
            <tab.icon size={14} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Search Tab - Recent searches + quick access */}
      {activeTab === 'search' && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Le mie ultime ricerche</h3>
          <div className="space-y-2">
            {mySearches.length === 0 ? (
              <div className="text-center py-12">
                <Search size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">Nessuna ricerca effettuata</p>
                <p className="text-xs text-slate-400 mt-1">Usa "Cerca su ANIA" per iniziare</p>
              </div>
            ) : mySearches.slice(0, 20).map(s => (
              <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 hover:shadow-sm transition-shadow cursor-pointer"
                onClick={() => handleViewVehicle(s.normalized_plate)} data-testid={`search-${s.id}`}>
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Car size={18} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm font-mono tracking-wider text-slate-900">{s.normalized_plate}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${s.result_status === 'success' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {s.result_status === 'success' ? 'Salvato' : s.result_status}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{s.action}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{s.reason}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <p className="text-xs text-slate-400">{fmtDate(s.created_at)}</p>
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={(e) => { e.stopPropagation(); openAniaWithPlate(s.normalized_plate); }}>
                    <ExternalLink size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Archive Tab */}
      {activeTab === 'archive' && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Veicoli in archivio ({myVehicles.length})</h3>
          <div className="space-y-2">
            {myVehicles.length === 0 ? (
              <div className="text-center py-12">
                <Database size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">Nessun veicolo in archivio</p>
              </div>
            ) : myVehicles.map(v => (
              <button key={v.normalized_plate} onClick={() => handleViewVehicle(v.normalized_plate)}
                className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 hover:shadow-sm transition-shadow">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Car size={18} className="text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-bold text-sm font-mono tracking-wider">{v.normalized_plate}</span>
                  <p className="text-xs text-slate-500">{v.reason}</p>
                </div>
                <ChevronRight size={16} className="text-slate-300" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Expiries Tab */}
      {activeTab === 'expiries' && (
        <div className="space-y-2">
          {expiries.length === 0 ? (
            <div className="text-center py-12">
              <Calendar size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Nessuna scadenza in archivio</p>
            </div>
          ) : expiries.map(v => (
            <div key={v.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 cursor-pointer hover:shadow-sm"
              onClick={() => setSelectedVehicle(v)}>
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                <Calendar size={18} className="text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-bold text-sm font-mono tracking-wider">{v.normalized_plate}</span>
                <p className="text-xs text-slate-500">{v.policy_company || '—'} · {v.brand || ''} {v.model || ''}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs text-slate-400">Scadenza</p>
                <p className="text-sm font-semibold text-slate-900">{v.policy_expiry ? fmtDateShort(v.policy_expiry) : '—'}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* All Searches Tab (Admin) */}
      {activeTab === 'all-searches' && isAdmin && (
        <div className="space-y-2">
          {allSearches.length === 0 ? (
            <div className="text-center py-12">
              <Eye size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Nessuna ricerca presente</p>
            </div>
          ) : allSearches.map(s => (
            <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                <Car size={18} className="text-slate-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm font-mono tracking-wider">{s.normalized_plate}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${s.result_status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{s.result_status}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">{s.user_name}</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{s.reason}</p>
              </div>
              <p className="text-xs text-slate-400 flex-shrink-0">{fmtDate(s.created_at)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Vehicle Detail Dialog */}
      <Dialog open={!!selectedVehicle} onOpenChange={() => setSelectedVehicle(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono tracking-wider">
              <Car size={20} className="text-blue-600" /> {selectedVehicle?.normalized_plate}
            </DialogTitle>
          </DialogHeader>
          {selectedVehicle && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Tipo', value: selectedVehicle.vehicle_type },
                  { label: 'Marca', value: selectedVehicle.brand },
                  { label: 'Modello', value: selectedVehicle.model },
                  { label: 'Compagnia', value: selectedVehicle.policy_company },
                  { label: 'N. Polizza', value: selectedVehicle.policy_number },
                  { label: 'Scadenza', value: selectedVehicle.policy_expiry ? fmtDateShort(selectedVehicle.policy_expiry) : null },
                  { label: 'Classe CU', value: selectedVehicle.risk_certificate?.cu_class },
                  { label: 'Ultimo controllo', value: selectedVehicle.last_checked_at ? fmtDate(selectedVehicle.last_checked_at) : null },
                ].map((item, i) => (
                  <div key={i} className="p-3 rounded-lg bg-slate-50">
                    <p className="text-[11px] text-slate-400">{item.label}</p>
                    <p className="text-sm font-semibold text-slate-800">{item.value || '—'}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={() => openAniaWithPlate(selectedVehicle.normalized_plate)}>
                  <ExternalLink size={14} className="mr-1.5" /> Apri su ANIA
                </Button>
                <Button size="sm" variant="outline" className="flex-1" onClick={() => {
                  setSaveForm({
                    plate: selectedVehicle.normalized_plate,
                    vehicle_type: selectedVehicle.vehicle_type || '',
                    brand: selectedVehicle.brand || '',
                    model: selectedVehicle.model || '',
                    policy_company: selectedVehicle.policy_company || '',
                    policy_number: selectedVehicle.policy_number || '',
                    policy_expiry: selectedVehicle.policy_expiry || '',
                    cu_class: selectedVehicle.risk_certificate?.cu_class || '',
                    reason: 'Aggiornamento dati',
                  });
                  setSelectedVehicle(null);
                  setSaveOpen(true);
                }}>
                  <RefreshCw size={14} className="mr-1.5" /> Aggiorna Dati
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Save Data Dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Save size={18} className="text-blue-600" /> Salva Dati ANIA</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Inserisci i dati dalla consultazione ANIA. Solo la targa e il motivo sono obbligatori.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Targa *</Label>
                <Input value={saveForm.plate} onChange={e => setSaveForm({ ...saveForm, plate: e.target.value.toUpperCase().replace(/\s/g, '') })}
                  placeholder="AA000BB" className="mt-1 font-mono font-bold tracking-wider" data-testid="save-plate" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Motivo *</Label>
                <Input value={saveForm.reason} onChange={e => setSaveForm({ ...saveForm, reason: e.target.value })}
                  placeholder="Preventivo RCA" className="mt-1" data-testid="save-reason" />
              </div>
              <div>
                <Label className="text-xs">Compagnia</Label>
                <Input value={saveForm.policy_company} onChange={e => setSaveForm({ ...saveForm, policy_company: e.target.value })} placeholder="Allianz, Generali..." className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Classe CU</Label>
                <Input value={saveForm.cu_class} onChange={e => setSaveForm({ ...saveForm, cu_class: e.target.value })} placeholder="1, 2, 14..." className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Scadenza Polizza</Label>
                <Input type="date" value={saveForm.policy_expiry} onChange={e => setSaveForm({ ...saveForm, policy_expiry: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">N. Polizza</Label>
                <Input value={saveForm.policy_number} onChange={e => setSaveForm({ ...saveForm, policy_number: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Marca</Label>
                <Input value={saveForm.brand} onChange={e => setSaveForm({ ...saveForm, brand: e.target.value })} placeholder="Fiat, BMW..." className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Modello</Label>
                <Input value={saveForm.model} onChange={e => setSaveForm({ ...saveForm, model: e.target.value })} placeholder="Panda, Serie 3..." className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Tipo Veicolo</Label>
                <Input value={saveForm.vehicle_type} onChange={e => setSaveForm({ ...saveForm, vehicle_type: e.target.value })} placeholder="Autovettura, Motociclo..." className="mt-1" />
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="btn-confirm-save">
              {saving ? 'Salvataggio...' : <><Save size={14} className="mr-1.5" /> Salva in Archivio</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings size={18} /> Impostazioni ANIA</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">URL Portale ANIA</Label>
              <Input value={portalUrl} onChange={e => setPortalUrl(e.target.value)} placeholder="https://amlogin.allianz.it/..." className="mt-1 text-xs" />
            </div>
            <Button onClick={async () => {
              try {
                await apiCall('/ania-allianz/credentials', { method: 'PUT', body: JSON.stringify({ portal_url: portalUrl }) });
                toast.success('URL aggiornato!'); setSettingsOpen(false);
              } catch (err) { toast.error(err.message); }
            }} className="w-full bg-slate-900 hover:bg-slate-800">
              Salva
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
